import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { SuiContainer, SuiField, SuiChipList } from '@splashthat/sui';
import { TRANSITION_TIMEOUT } from 'common/constants';
import { MULTIPLE_OPERATOR_SEPARATOR } from 'common/entities/formLogicRule/constants';
import './FormLogicValueSet.styl';

/**
 * @param {string|number} operandRight - falling back to empty string just in case undefined
 * @param {Object|null} operandOptions
 */
const formatValueSet = (operandRight = '', operandOptions = {}) =>
    `${operandRight}`
        .trim()
        .split(MULTIPLE_OPERATOR_SEPARATOR)
        .map((value) => {
            const operandOption = Object.values(operandOptions).find((operand) => operand.value === value);

            return {
                label: (operandOption && operandOption.label) || value,
                value,
                active: true,
            };
        });

export const isValidValue = (value) => Boolean(value) || value === 0;

const BASE_CLASS_NAME = 'FormLogicValueSet';

export default class FormLogicValueSet extends Component {
    static propTypes = {
        children: PropTypes.node.isRequired,
        allowMultipleInput: PropTypes.bool.isRequired,
        operandRight: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]).isRequired,
        handleChangeOperandRight: PropTypes.func.isRequired,
        operandOptions: PropTypes.object,
    };

    static defaultProps = {
        operandOptions: undefined,
    };

    state = {
        valueSet: this.props.allowMultipleInput
            ? formatValueSet(this.props.operandRight, this.props.operandOptions)
            : [],
    };

    static getDerivedStateFromProps(nextProps, prevState) {
        if (!nextProps.allowMultipleInput) {
            return null;
        }
        const valueSetFromProps = formatValueSet(nextProps.operandRight, nextProps.operandOptions);

        return !valueSetFromProps.length
            ? {
                  ...prevState,
                  valueSet: valueSetFromProps,
              }
            : { ...prevState };
    }

    /**
     * Set the new valueSet as the operandRight value so we can save it.
     * @param {array} valueSet Joined with ',' so values can be separated with spaces.
     */
    handleNewValueSet = (valueSet) => {
        const newValueSet = valueSet.map((chip) => chip.value).join(MULTIPLE_OPERATOR_SEPARATOR);
        this.props.handleChangeOperandRight(null, newValueSet);
    };

    /**
     * Checks if the value is repeated before setting it as inactive, to then
     * set active to true and trigger the transition.
     * @param {string|Object} { label, value } suggestion object or string value.
     */
    addValueToSet = (newValue) => {
        const label = typeof newValue === 'string' ? newValue : newValue.label;
        const value = typeof newValue === 'string' ? newValue : newValue.value;

        const trimmedValue = typeof value === 'string' ? value.trim() : value;
        const valueIsValid = isValidValue(trimmedValue);
        const isRepeated = this.state.valueSet.find((chip) => chip.value === value);
        if (!isRepeated && valueIsValid) {
            this.setState(
                (prevState) => ({
                    ...prevState,
                    valueSet: [
                        ...prevState.valueSet,
                        {
                            label,
                            value,
                            active: false,
                        },
                    ],
                }),
                () => {
                    this.handleNewValueSet(this.state.valueSet);
                    const activeValueSet = this.state.valueSet.map((val) =>
                        val.value === value
                            ? {
                                  ...val,
                                  active: true,
                              }
                            : val,
                    );
                    this.setState((prevState) => ({
                        ...prevState,
                        valueSet: activeValueSet,
                    }));
                },
            );
        }
    };

    /**
     * Filter values and set as valueSet all the active ones.
     * @param {array} disabledValueSet
     */
    removeValueFromSet = (disabledValueSet) => {
        const filteredValueSet = disabledValueSet.filter((val) => val.active);
        this.setState((prevState) => ({
            ...prevState,
            valueSet: filteredValueSet,
        }));
        this.handleNewValueSet(filteredValueSet);
    };

    /**
     * Retrieves the index and chip from SuiChipList, sets the given chip as inactive first
     * so the transition triggers, and removes it after the timeout.
     * @param {number} index
     * @param {Object} chip { label, value } Always includes label, vslaue is optional.
     */
    onRemoveValueFromSet = (index, { label, value }) => {
        const disabledValueSet = this.state.valueSet.map((val) => ({
            ...val,
            active: val.value !== value,
        }));
        this.setState(
            (prevState) => ({ ...prevState, valueSet: disabledValueSet }),
            // @TODO When updating SuiChipList to handle its own transition, update this function.
            () =>
                setTimeout(() => {
                    this.removeValueFromSet(disabledValueSet);
                }, TRANSITION_TIMEOUT),
        );
    };

    render() {
        return (
            <SuiContainer>
                {React.Children.map(this.props.children, (child) =>
                    React.cloneElement(child, {
                        ...this.props,
                        addValueToSet: this.addValueToSet,
                    }),
                )}
                {this.props.allowMultipleInput && (
                    <SuiField
                        className={`${BASE_CLASS_NAME}__chipList`}
                        formElement={<SuiChipList chips={this.state.valueSet} onRemove={this.onRemoveValueFromSet} />}
                    />
                )}
            </SuiContainer>
        );
    }
}
